import { useEffect, useState } from "react"
import { useSession } from "../auth/session"
import "./LoginPage.css"

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const REMEMBERED_EMAIL_KEY = "vantero.menu.rememberedEmail"

export default function LoginPage() {
  const { signIn } = useSession()
  const [email, setEmail] = useState(() => localStorage.getItem(REMEMBERED_EMAIL_KEY) ?? "")
  const [password, setPassword] = useState("")
  const [rememberMe, setRememberMe] = useState(() => Boolean(localStorage.getItem(REMEMBERED_EMAIL_KEY)))
  const [isPasswordHidden, setIsPasswordHidden] = useState(true)
  const [emailError, setEmailError] = useState("")
  const [passwordError, setPasswordError] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isLoginEnabled = email.trim().length > 0 && password.length > 0 && !isSubmitting

  // Keyboards on iOS shrink the visual viewport; keep the focused field in view.
  useEffect(() => {
    const viewport = window.visualViewport
    if (!viewport) return undefined
    const syncInset = () => {
      const inset = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)
      document.documentElement.style.setProperty("--keyboard-inset", `${inset}px`)
    }
    viewport.addEventListener("resize", syncInset)
    viewport.addEventListener("scroll", syncInset)
    syncInset()
    return () => {
      viewport.removeEventListener("resize", syncInset)
      viewport.removeEventListener("scroll", syncInset)
      document.documentElement.style.removeProperty("--keyboard-inset")
    }
  }, [])

  const handleSubmit = async (event) => {
    event.preventDefault()
    setPasswordError("")

    if (!EMAIL_PATTERN.test(email.trim())) {
      setEmailError("Invalid email address")
      return
    }
    setEmailError("")

    setIsSubmitting(true)
    try {
      await signIn({ email, password })
      if (rememberMe) localStorage.setItem(REMEMBERED_EMAIL_KEY, email.trim())
      else localStorage.removeItem(REMEMBERED_EMAIL_KEY)
    } catch (error) {
      setPasswordError(error.message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="login">
      <div className="login__scroll scroll-y">
        <div className="login__spacer">
          <img
            className="login__background"
            src="/img/login-background.jpg"
            alt=""
            aria-hidden="true"
          />
        </div>
        <form className="login__sheet" onSubmit={handleSubmit} noValidate>
          <h1 className="login__title t-y24">Welcome Back</h1>
          <p className="login__subtitle t-p12">
            Log in to your account and browse your restaurant menu
          </p>

          <label className="login__label t-p16" htmlFor="login-email">
            Email<span className="login__required">*</span>
          </label>
          <div className="login__field">
            <input
              id="login-email"
              className={`login__input t-p16${emailError ? " login__input--invalid" : ""}`}
              type="email"
              inputMode="email"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              placeholder="xyz@gmail.com"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value)
                setEmailError("")
              }}
            />
            {emailError && (
              <span className="login__field-badge" aria-hidden="true">
                <WarningIcon />
              </span>
            )}
          </div>
          {emailError && <p className="login__error t-p12">{emailError}</p>}

          <label className="login__label login__label--password t-p16" htmlFor="login-password">
            Password<span className="login__required">*</span>
          </label>
          <div className="login__field">
            <input
              id="login-password"
              className="login__input t-p16"
              type={isPasswordHidden ? "password" : "text"}
              autoComplete="current-password"
              placeholder="********"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value)
                setPasswordError("")
              }}
            />
            <button
              type="button"
              className="login__eye"
              aria-label={isPasswordHidden ? "Show password" : "Hide password"}
              onClick={() => setIsPasswordHidden((hidden) => !hidden)}
            >
              <EyeIcon crossed={isPasswordHidden} />
            </button>
          </div>
          {passwordError && (
            <p className="login__error t-p12" role="alert">
              {passwordError}
            </p>
          )}

          <div className="login__row">
            <button
              type="button"
              className="login__remember t-p16"
              aria-pressed={rememberMe}
              onClick={() => setRememberMe((on) => !on)}
            >
              <img
                src={rememberMe ? "/img/checkbox-fill.png" : "/img/checkbox-unfill.png"}
                alt=""
                aria-hidden="true"
              />
              Remember Me
            </button>
            <a
              className="login__forgot t-p16"
              href="https://vantero.ai"
              onClick={(event) => event.preventDefault()}
              title="Reset your password in the Vantero app"
            >
              Forgot Password?
            </a>
          </div>

          <button
            type="submit"
            className={`login__submit t-p16${isLoginEnabled ? "" : " login__submit--disabled"}`}
            disabled={!isLoginEnabled}
          >
            {isSubmitting ? "Logging in…" : "Login"}
          </button>
        </form>
      </div>
    </div>
  )
}

function WarningIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none">
      <path
        d="M12 6.5v7"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      <circle cx="12" cy="17.4" r="1.35" fill="currentColor" />
    </svg>
  )
}

function EyeIcon({ crossed }) {
  return (
    <svg viewBox="0 0 24 24" fill="none">
      <path
        d="M2.5 12s3.6-6 9.5-6 9.5 6 9.5 6-3.6 6-9.5 6-9.5-6-9.5-6Z"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <circle cx="12" cy="12" r="2.6" stroke="currentColor" strokeWidth="1.6" />
      {crossed && (
        <path d="m4 4 16 16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      )}
    </svg>
  )
}
